import React, { useState, useEffect, useRef, useMemo } from 'react'
import CurvedTriangle from '@/images/customSvg/buttonCurve'
import { Info, X } from 'lucide-react'
import {
  Student,
  Course,
  Subscription,
  InstructorLetTraining,
  CompareUsers,
} from '@/types/EnrollLearners'

import InstructorEventDate from '../../components/Search/InstructorEventDate'
import { useAuth } from '@/hooks/useAuth'
import { toast } from '@/components/ui/use-toast'
import { useEnrollments } from '@/hooks/enrollment'
import Tooltip from '@mui/material/Tooltip';
interface SelectedSession {
  courseId: string
  sessionId: string
}

interface EnrollmentTabsProps {
  activeTab: 'instructor' | 'online'
  onTabChange: (tab: 'instructor' | 'online') => void
  loadingBranches: boolean
  loadingSubscriptions: boolean
  loadingContent: boolean
  setloadingContent: (loading: boolean) => void
  subscriptions: Subscription[]
  selectedSubscription: string
  onSubscriptionChange: (value: string) => void
  searchSessions: string
  onSearchSessionsChange: (value: string) => void
  sessions: Course[]
  selectedSession: SelectedSession | null
  onSessionSelect: (session: SelectedSession | null) => void
  learners: Student[]
  loadingStudentsContent: boolean
  compareUsers: CompareUsers[]
  searchLearners: string
  onSearchLearnersChange: (value: string) => void
  enrolledLearners: Set<string>
  enrollingLearners: Set<string>
  onEnroll: (learnerId: string) => void
  onUnenroll: (learnerId: string) => void
  instructorLetTraining: InstructorLetTraining[]
  courses: Course[]
  learningPlans: Course[]
  onEnrollOnline: (
    contentIds: string[],
    learnerIds: string[],
    contentType: 'course' | 'learning_plan'
  ) => Promise<any>
}

const EnrollmentTabs: React.FC<EnrollmentTabsProps> = ({
  activeTab,
  onTabChange,
  loadingBranches,
  loadingSubscriptions,
  loadingContent,
  setloadingContent: setLoadingContent,
  subscriptions,
  selectedSubscription,
  onSubscriptionChange,
  searchSessions,
  onSearchSessionsChange,
  sessions,
  selectedSession,
  onSessionSelect,
  learners,
  loadingStudentsContent,
  compareUsers,
  searchLearners,
  onSearchLearnersChange,
  enrolledLearners,
  enrollingLearners,
  onEnroll,
  onUnenroll,
  instructorLetTraining,
  courses,
  learningPlans,
  onEnrollOnline,
}) => {
  let globalIndex = 0
  const [activeField, setActiveField] = useState(null)
  const [activeTrainingField, setActiveTrainingField] = useState(null)
  const wrapperRef = useRef(null)
  const { currentUser } = useAuth()

  const {
    enrollSessionsForLearners,
    unEnrollSessionsForLearners,
  } = useEnrollments()

  const [contentTab, setContentTab] = useState('learning_plans')
  const [searchContent, setSearchContent] = useState('')
  const [selectedContent, setSelectedContent] = useState([])
  const [selectedLearners, setSelectedLearners] = useState([])
  const [isEnrolling, setIsEnrolling] = useState(false)
  const [isAnyLoading, setIsAnyLoading] = useState(false)
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null)
  const [isMdUp, setIsMdUp] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [searchInstructorSessions, setSearchInstructorSessions] = useState('')
  const [localEnrolledSessions, setLocalEnrolledSessions] = useState(new Set<string>())

  const shapes = ['circle', 'square', 'rhombus', 'polygons', 'triangle']
  const ShapeIcon = ({ type }) => {
    const base = 'inline-block w-[9px] h-[9px]'

    switch (type) {
      case 'circle':
        return <div className={`${base} rounded-full bg-black`} />
      case 'square':
        return <div className={`${base} bg-black`} />
      case 'rhombus':
        return <div className={`${base} bg-black rotate-45`} />
      case 'triangle':
        return (
          <div
            className="w-0 h-0 
                      border-l-[5px] border-r-[5px] 
                      border-b-[9px] 
                      border-l-transparent border-r-transparent 
                      border-b-black"
          />
        )
      case 'polygons':
        return (
          <div
            className="inline-block w-[10px] h-[10px] bg-black"
            style={{
              clipPath:
                'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
            }}
          />
        )

      default:
        return <div className={`${base} bg-black`} />
    }
  }

  const compareArray = useMemo(() => {
    if (!compareUsers) return []
    return Array.isArray(compareUsers) ? compareUsers : compareUsers || []
  }, [compareUsers])

  const enrolledMap = useMemo(() => {
    const set = new Set<string>()
    compareArray.forEach((u: any) => {
      if (String(u.status).toLowerCase() === 'enrolled') {
        set.add(String(u.user_id))
      }
    })
    return set
  }, [compareArray])

  const sortedLearners = useMemo(() => {
    return [...learners].sort((a, b) => {
      const aEnrolled = enrolledMap.has(String(a.id)) ? 1 : 0
      const bEnrolled = enrolledMap.has(String(b.id)) ? 1 : 0

      if (bEnrolled !== aEnrolled) {
        return bEnrolled - aEnrolled
      }
      return a.first_name.localeCompare(b.first_name)
    })
  }, [learners, enrolledMap])

  const filteredInstructorTraining = useMemo(() => {
    if (!instructorLetTraining || instructorLetTraining.length === 0) {
      return []
    }

    // Flatten all sessions from all courses and sort by earliest event
    const allSessionsFlattened = instructorLetTraining.flatMap((instructor) =>
      instructor.course_sessions.map((session) => ({
        ...instructor,
        course_sessions: [session], // Keep only this session
      }))
    )

    // Sort all flattened sessions by their earliest event date+time
    const sortedSessions = allSessionsFlattened
      .map((instructor) => ({
        ...instructor,
        course_sessions: instructor.course_sessions.map((session) => ({
          ...session,
          course_events: [...(session.course_events || [])].sort((a, b) => {
            const aDateTime = new Date(`${a.event_date}T${a.event_start_time}`)
            const bDateTime = new Date(`${b.event_date}T${b.event_start_time}`)
            return aDateTime.getTime() - bDateTime.getTime()
          }),
        })),
      }))
      .sort((a, b) => {
        const aFirstEvent = a.course_sessions[0]?.course_events?.[0]
        const bFirstEvent = b.course_sessions[0]?.course_events?.[0]

        if (!aFirstEvent) return 1
        if (!bFirstEvent) return -1

        const aDateTime = new Date(
          `${aFirstEvent.event_date}T${aFirstEvent.event_start_time}`
        )
        const bDateTime = new Date(
          `${bFirstEvent.event_date}T${bFirstEvent.event_start_time}`
        )

        return aDateTime.getTime() - bDateTime.getTime()
      })

    // If no search, return sorted data
    if (!searchInstructorSessions.trim()) {
      return sortedSessions
    }

    // Apply search filter
    const query = searchInstructorSessions.toLowerCase()

    return sortedSessions.filter((instructor) => {
      const courseMatches = instructor.course_name?.toLowerCase().includes(query)

      const session = instructor.course_sessions[0]
      const sessionNameMatches = session.course_session_name
        ?.toLowerCase()
        .includes(query)

      const locationMatches = session.course_locations?.some(
        (loc) =>
          loc.location_name?.toLowerCase().includes(query) ||
          loc.location_address?.toLowerCase().includes(query)
      )

      const dateMatches = session.course_events?.some(
        (event) =>
          event.event_start_time?.includes(query) ||
          event.event_end_time?.includes(query)
      )

      return courseMatches || sessionNameMatches || locationMatches || dateMatches
    })
  }, [searchInstructorSessions, instructorLetTraining])

  // Instructor led Training Subscriptions
  const iltViltSubs = useMemo(
    () => subscriptions.filter((s) => ['ILT', 'VILT'].includes(s.plan_type)),
    [subscriptions]
  )

  // Instructor led Training Subscriptions
  const onlineLearningSubs = useMemo(
    () => subscriptions.filter((s) => s.plan_type === 'OLT'),
    [subscriptions]
  )

  const primaryColor = `hsl(${getComputedStyle(document.documentElement)
    .getPropertyValue('--primary')
    .trim()})`

  const secondaryColor = `hsl(${getComputedStyle(document.documentElement)
    .getPropertyValue('--secondary')
    .trim()})`

  const filteredCourses = useMemo(() => {
    const q = searchContent.toLowerCase()
    return courses.filter(
      (c) =>
        c.item_name.toLowerCase().includes(q) ||
        c.item_code.toLowerCase().includes(q)
    )
  }, [searchContent, courses])

  const filteredLearningPlans = useMemo(() => {
    const q = searchContent.toLowerCase()
    return learningPlans.filter(
      (lp) =>
        lp.item_name.toLowerCase().includes(q) ||
        lp.item_code.toLowerCase().includes(q)
    )
  }, [searchContent, learningPlans])

  const currentContent =
    contentTab === 'courses' ? filteredCourses : filteredLearningPlans

const filteredLearnersList = useMemo(() => {
    const q = searchLearners.toLowerCase()
    return learners
      .filter(
        (s) =>
          s.first_name.toLowerCase().includes(q) ||
          s.last_name.toLowerCase().includes(q)
      )
      .sort((a, b) => {
        // true first (descending boolean)
        if (a.is_enrollable !== b.is_enrollable) {
          return b.is_enrollable ? 1 : -1;
        }
        // then name ascending
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [searchLearners, learners])

  // Reset selections when changing tabs or subscriptions
  useEffect(() => {
    setSelectedContent([])
    setSelectedLearners([])
  }, [contentTab, selectedSubscription, activeTab])

  const handleContentToggle = (id) => {
    setSelectedContent((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const handleLearnerToggle = (id) => {
    setSelectedLearners((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  // Handle Online Training enrollment
  const handleEnrollClick = async () => {
    if (selectedContent.length > 0 && selectedLearners.length > 0) {
      setIsEnrolling(true)
      try {
        const contentType =
          contentTab === 'courses' ? 'course' : 'learning_plan'
        await onEnrollOnline(selectedContent, selectedLearners, contentType)
        setSelectedContent([])
        setSelectedLearners([])
      } catch (error) {
        console.error('Enrollment error:', error)
      } finally {
        setIsEnrolling(false)
      }
    }
  }

  // Handle Online Training enrollment from Technician view
  const handleEnrollILTTechnician = async (courseID: string, sessionID: number) => {
    if (isAnyLoading) return
    const sessionIdStr = `${sessionID}`
    setIsAnyLoading(true)
    setLoadingSessionId(sessionIdStr)

    try {
      const transactionCode = subscriptions.find(
        (s) => s.id === selectedSubscription
      )?.transaction_code

      const response = await enrollSessionsForLearners(
        courseID,
        sessionIdStr,
        [currentUser.id],
        transactionCode
      )
      if (!response.success) {
        toast({
          title: 'Already Enrolled',
          description: response.errors[0],
          variant: 'destructive',
        })
        return
      } else {
        setLocalEnrolledSessions(prev => new Set(prev).add(sessionIdStr))
        toast({
          title: 'Success!',
          description: 'Successfully enrolled.',
        })
      }
    } catch (err) {
      console.error('Enrollment failed:', err)
      toast({
        title: 'Uh Oh!',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoadingSessionId(null)
      setIsAnyLoading(false)
    }
  }

  const handleUnenrollILTTechnician = async (courseID: string, sessionID: number) => {
    if (isAnyLoading) return
    const sessionIdStr = `${sessionID}`
    setIsAnyLoading(true)
    setLoadingSessionId(sessionIdStr)

    try {
      const transactionCode = subscriptions.find(
        (s) => s.id === selectedSubscription
      )?.transaction_code

      const response = await unEnrollSessionsForLearners(
        courseID,
        sessionIdStr,
        currentUser.id,
        transactionCode
      )
      if (!response.success) {
        toast({
          title: 'Unenrollment Failed',
          description: response.errors[0],
          variant: 'destructive',
        })
        return
      } else {
        setLocalEnrolledSessions(prev => {
          const newSet = new Set(prev)
          newSet.delete(sessionIdStr)
          return newSet
        })
        toast({
          title: 'Success!',
          description: 'Successfully unenrolled.',
        })
      }
    } catch (err) {
      console.error('Unenrollment failed:', err)
      toast({
        title: 'Uh Oh!',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoadingSessionId(null)
      setIsAnyLoading(false)
    }
  }

  // Handle Online Training enrollment from Technician view
  const handleEnrollOLTTechnician = async () => {
    console.log("currentUser.id", currentUser.id)
    console.log("selectedContent", selectedContent)
    if (selectedContent.length > 0) {
      setIsEnrolling(true)
      try {
        const contentType =
          contentTab === 'courses' ? 'course' : 'learning_plan'
        await onEnrollOnline(selectedContent, [`${currentUser.id}`], contentType)
        setSelectedContent([])
        setSelectedLearners([])
      } catch (error) {
        console.error('Enrollment error:', error)
      } finally {
        setIsEnrolling(false)
      }
    }
  }

  const isEnrollDisabled =
    selectedContent.length === 0 || selectedLearners.length === 0 || isEnrolling

  // Detect screen size
  useEffect(() => {
    const handleResize = () => setIsMdUp(window.innerWidth >= 768)
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Auto-select subscription if only one is available
  useEffect(() => {
    if (
      activeTab === 'instructor' &&
      iltViltSubs.length === 1 &&
      !selectedSubscription
    ) {
      setLoadingContent(true)

      //defer selection to next tick
      setTimeout(() => {
        onSubscriptionChange(iltViltSubs[0].id)
      }, 0)
    }
  }, [activeTab, iltViltSubs, selectedSubscription, onSubscriptionChange])

  // Sync local enrolled sessions from props when instructorLetTraining changes
  useEffect(() => {
    if (instructorLetTraining) {
      const enrolledSet = new Set<string>()
      instructorLetTraining.forEach(instructor =>
        instructor.course_sessions.forEach(session => {
          if (session.is_enrolled) {
            enrolledSet.add(`${session.course_session_id}`)
          }
        })
      )
      setLocalEnrolledSessions(enrolledSet)
    }
  }, [instructorLetTraining])

  // Auto-select subscription for online training
  useEffect(() => {
    if (
      activeTab === 'online' &&
      onlineLearningSubs.length === 1 &&
      !selectedSubscription
    ) {
      setLoadingContent(true)

      //defer selection to next tick
      setTimeout(() => {
        onSubscriptionChange(onlineLearningSubs[0].id)
      }, 0)
    }
  }, [
    activeTab,
    onlineLearningSubs,
    selectedSubscription,
    onSubscriptionChange,
  ])

  return (
    <div className="w-full">
      <div className="flex items-end">
        <button
          onClick={() => onTabChange('instructor')}
          className={`relative inline-block px-6 py-3 font-semibold text-white transition-all overflow-hidden
          ${activeTab === 'instructor'
              ? 'bg-primary border-b-[0.5px] border-primary'
              : 'bg-secondary border-b-[0.5px] border-gray-400'
            }
          rounded-t`}
        >
          <div className="flex items-center">
            Instructor-Led Training
            {iltViltSubs.length > 0 && (
              <span
                className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold
                ${activeTab === 'instructor' ? 'bg-secondary' : 'bg-primary'}
                `}
              >
                {iltViltSubs.length}
              </span>
            )}
          </div>

        </button>
        <div
          className={`relative bottom-[0px] -ml-[3px] 
          ${activeTab === 'instructor'
              ? 'border-b-[0.5px] border-primary' : 'border-b-[0.5px] border-gray-400'}`}
        >
          <CurvedTriangle
            direction="right"
            color={activeTab === 'instructor' ? primaryColor : secondaryColor}
            width={10}
            height={20}
          />
        </div>
        <div
          className={`relative bottom-[0px] -mr-[3px] 
          ${activeTab === 'online'
              ? 'border-b-[0.5px] border-primary'
              : 'border-b-[0.5px] border-gray-400'
            }`}
        >
          <CurvedTriangle
            direction="left"
            color={activeTab === 'online' ? primaryColor : secondaryColor}
            width={10}
            height={20}
          />
        </div>

        <button
          onClick={() => onTabChange('online')}
          className={`relative inline-block px-6 py-3 font-semibold text-white transition-all overflow-hidden
          ${activeTab === 'online'
              ? 'bg-primary border-b-[0.5px] border-primary'
              : 'bg-secondary border-b-[0.5px] border-gray-400'
            }
          rounded-t`}
        >
          <div className="flex items-center">
            Online Training
            {onlineLearningSubs.length > 0 && (
              <span
                className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold
                    ${activeTab === 'online'
                    ? 'bg-secondary'
                    : 'bg-primary'
                  }`}
              >
                {onlineLearningSubs.length}
              </span>
            )}
          </div>
        </button>

        <div
          className={`relative bottom-[0px] -ml-[3px] 
          ${activeTab === 'online'
              ? 'border-b-[0.5px] border-primary'
              : 'border-b-[0.5px] border-gray-400'
            }`}
        >
          <CurvedTriangle
            direction="right"
            color={activeTab === 'online' ? primaryColor : secondaryColor}
            width={10}
            height={20}
          />
        </div>
      </div>

      <div
        className={`border-4 rounded rounded-tl-none p-1 border-primary bg-primary`}
      >
        <div className="flex gap-1 lg:container flex-col lg:flex-row">
          {activeTab === 'instructor' && (
            <>
              <div className="flex-1 border-2 border-black bg-gray-100 p-4 rounded w-full flex flex-col">
                {loadingBranches ? (
                  <h3 className="text-xl font-bold flex items-center text-primary">
                    <span className="ml-1 animate-pulse">
                      Loading branches...
                    </span>
                  </h3>
                ) : (
                  <>
                    <h2 className="text-md font-bold text-gray-800">
                      Upcoming Training Sessions
                    </h2>

                    {loadingSubscriptions ? (
                      <h3 className="text-xl font-bold flex items-center text-primary">
                        <span className="ml-1 animate-pulse">
                          Loading Subscriptions...
                        </span>
                      </h3>
                    ) : iltViltSubs.length == 0 ? (
                      <p>
                        <span className="text-lg font-semibold text-secondary">
                          No Instructor-Led Training Subscriptions Found{' '}
                        </span>
                      </p>
                    ) : (
                      <div>
                        <div
                          className={` text-xs text-gray-600 flex justify-end items-center mt-0 ${activeField === 'filter'
                            ? 'w-full'
                            : 'w-full md:w-[48%] pl-0'
                            }`}
                        >
                          <span className={`ml-auto mr-0`}>
                            {!loadingBranches &&
                              !loadingSubscriptions &&
                              iltViltSubs.length > 0 &&
                              activeField != 'search' ? (
                              <span>{iltViltSubs.length} Available</span>
                            ) : (
                              <span>&nbsp;</span>
                            )}
                          </span>
                        </div>

                        <div
                          ref={wrapperRef}
                          style={{
                            justifyContent:
                              activeField === 'filter'
                                ? 'flex-start'
                                : 'flex-end',
                          }}
                          className="flex flex-col xs:mt-1 lg:mt-0 md:flex-row md:items-center md:gap-4 w-full transition-all mb-2"
                        >
                          {(activeField === null ||
                            activeField === 'filter') && (
                              <div
                                className={`relative transition-all duration-300 mb-1 md:mb-0 ${activeField === 'filter'
                                  ? 'w-full'
                                  : 'w-full md:w-1/2'
                                  }`}
                                onMouseEnter={
                                  isMdUp
                                    ? () => setActiveField('filter')
                                    : undefined
                                }
                                onMouseLeave={
                                  isMdUp ? () => setActiveField(null) : undefined
                                }
                              >
                                <select
                                  title="Filter By Subscription"
                                  value={selectedSubscription}
                                  onChange={(e) =>
                                    onSubscriptionChange(e.target.value)
                                  }
                                  className="w-full px-3 py-1 pr-8 border border-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white text-sm text-gray-700"
                                >
                                  <option value="">
                                    ▼ Filter By Subscription
                                  </option>
                                  {iltViltSubs.map((iltSubs) => (
                                    <option key={iltSubs.id} value={iltSubs.id}>
                                      {iltSubs.plan_name} | {iltSubs.plan_code}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                          {(activeField === null ||
                            activeField === 'search') && (
                              <div
                                className={`relative transition-all duration-300 ${activeField === 'search'
                                  ? 'w-full'
                                  : 'w-full md:w-1/2'
                                  }`}
                                onMouseEnter={
                                  isMdUp
                                    ? () => setActiveField('search')
                                    : undefined
                                }
                                onMouseLeave={
                                  isMdUp
                                    ? (e) => {
                                      if (
                                        !e.currentTarget.contains(
                                          document.activeElement
                                        ) &&
                                        activeField === 'search'
                                      ) {
                                        setActiveField(null)
                                      }
                                    }
                                    : undefined
                                }
                              >
                                <input
                                  type="text"
                                  placeholder="Search Sessions"
                                  value={searchInstructorSessions}
                                  onChange={(e) =>
                                    setSearchInstructorSessions(e.target.value)
                                  }
                                  className="w-full px-3 py-1 pr-8 border border-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white text-sm text-gray-600"
                                />

                                {activeField === 'search' &&
                                  searchInstructorSessions && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        onSearchSessionsChange('')
                                        setSearchInstructorSessions('')
                                        setActiveField(null)
                                      }}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                      <X size={16} />
                                    </button>
                                  )}
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="bg-white border-2 border-gray-400 rounded p-5 max-h-[20rem] lg:max-h-[32rem] overflow-y-auto flex flex-grow">
                  <div className="space-y-1 flex-grow">
                    {loadingContent ? (
                      <div className="flex items-center justify-center w-full h-[30rem]">
                        <div className="flex flex-col items-center justify-center">
                          <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent mb-4"></div>
                          <p className="text-lg font-semibold text-gray-800">
                            Loading Sessions...
                          </p>
                        </div>
                      </div>
                    ) :
                      iltViltSubs.length == 0 ? (
                        <div className="text-center py-4 text-gray-500 text-sm h-[30rem] items-center flex justify-center">
                          <div className="flex flex-col items-center gap-2">
                            <>
                              <p className="text-base font-semibold text-gray-700">
                                No Sessions Available
                              </p>
                            </>
                          </div>
                        </div>
                      ) :
                      filteredInstructorTraining.length > 0 ? (
                          filteredInstructorTraining.map((instructor) =>
                            instructor.course_sessions
                              // .filter(
                              //   (session) => session.course_is_private !== true
                              // )
                              .map((session) => {
                                const currentIndex = globalIndex++
                              // const isLevel3 = currentUser.user_level == 3
                              const isTechnician = currentUser.user_level == 6

                              return (
                                <div
                                  key={`${instructor.course_id}-${session.course_session_id}`}
                                  className={`p-1 rounded transition-all 
                                    ${
                                      !isTechnician &&
                                      selectedSession?.sessionId ===
                                        session.course_session_id.toString()
                                        ? 'bg-blue-100'
                                        : currentIndex % 2 === 0
                                        ? 'bg-white'
                                        : 'bg-gray-200'
                                    }
                                  `}
                                  onClick={() => {
                                    if (!isTechnician) {
                                      if (
                                        selectedSession?.sessionId ===
                                        session.course_session_id.toString()
                                      ) {
                                        onSessionSelect(null)
                                      } else {
                                        onSessionSelect({
                                          courseId: instructor.course_id.toString(),
                                          sessionId: session.course_session_id.toString(),
                                        })
                                      }
                                    }
                                  }}
                                >
                                  <div className="flex items-center gap-3 justify-between">
                                    
                                    {/* LEFT SIDE */}
                                    <div className="flex items-center gap-3 flex-1">
                                      
                                      {/* Checkbox ONLY for power user or super admin */}
                                      {!isTechnician  && (
                                        <input
                                          title="select session"
                                          aria-label={`Select session ${session.course_session_name}`}
                                          type="checkbox"
                                          checked={
                                            selectedSession?.sessionId ===
                                            session.course_session_id.toString()
                                          }
                                          onChange={() => {
                                            if (
                                              selectedSession?.sessionId ===
                                              session.course_session_id.toString()
                                            ) {
                                              onSessionSelect(null)
                                            } else {
                                              onSessionSelect({
                                                courseId: 
                                                instructor.course_id.toString(),
                                                sessionId:
                                                  session.course_session_id.toString(),
                                              })
                                            }
                                          }}
                                          className="w-4 h-4 cursor-pointer accent-primary rounded-full"
                                        />
                                      )}

                                      {/* CONTENT */}
                                      <div className="flex-1">
                                        <h3 className="font-semibold">
                                          <span className="text-gray-900">
                                            {instructor.course_name}
                                          </span>

                                                                                      {/* <span className="text-gray-600">
                                        {' '}
                                        ({session.course_session_name}){' '}
                                      </span> */}
                                        </h3>

                                        <div className="grid grid-cols-[70px_1fr] gap-x-2 gap-y-1 text-sm text-gray-700">
                                          {/* Enrolled */}
                                          {/* <div className="font-semibold text-gray-900">
                                      Enrolled:
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                      <span key={`enrolled}`}>
                                        {session.course_num_enrolled} /{' '}
                                        {session.course_max_enrollment}
                                      </span>
                                    </div> */}
                                         
                                          {/* When */}
                                          <div className="font-semibold">When:</div>
                                          <div className="flex flex-col gap-0.5">

                                            {session.course_events.map((event, index) => {
                                              const locationIndex = session.course_locations.findIndex(
                                                  loc => loc.location_id === event.event_location_id
                                                );
                                              const shapeType = shapes[locationIndex % shapes.length];

                                              return (
                                                <div
                                                  key={index}
                                                  className={`flex items-start ${session.course_events.length > 1 ? 'gap-2' : 'gap-0'}`}
                                                >
                                                  {session.course_events.length > 
                                                  1 &&
                                                    session.course_locations
                                                    .length > 1 && (
                                                      <div>
                                                        <ShapeIcon type={shapeType} />
                                                      </div>
                                                    )}

                                                  <span className="block leading-tight">
                                                    <InstructorEventDate 
                                                    event={event} 
                                                    />
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                          {/* Where */}
                                          <div className="font-semibold">
                                            Where:
                                            </div>
                                          <div className="flex flex-col gap-0.5">
                                            {instructor.course_type?.toLowerCase() ===
                                            'vilt' ? (
                                              <div className="block leading-tight">
                                                Virtual Classroom | Zoom
                                              </div>
                                            ) : (
                                              session.course_locations.map(
                                                (loc, index) => (
                                                <div
                                                  key={index}
                                                  className={`flex items-start ${session.course_locations.length > 1 ? 'gap-2' : 'gap-0'}`}
                                                >
                                                  <div>
                                                    {session.course_locations
                                                    .length > 1 && (
                                                      <ShapeIcon
                                                        type={
                                                          shapes[
                                                            index % shapes.length
                                                          ]
                                                        }
                                                      />
                                                    )}
                                                  </div>

                                                  <div className="block leading-tight">
                                                    {loc.location_name},{' '}
                                                    {loc.location_address}
                                                  </div>
                                                </div>
                                              )
                                              )
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Button Logic for Techinician */}
                                    {isTechnician && (() => {
                                        const sessionIdStr = `${session.course_session_id}`
                                        const isLocalEnrolled = localEnrolledSessions.has(sessionIdStr)
                                        return (
                                          <button 
                                            onClick={() => {
                                              if (isLocalEnrolled) {
                                                handleUnenrollILTTechnician(instructor.course_id, session.course_session_id)
                                              } else {
                                                handleEnrollILTTechnician(instructor.course_id, session.course_session_id)
                                              }
                                            }} 
                                            disabled={loadingSessionId === sessionIdStr || isAnyLoading}
                                            className={`px-4 py-1 rounded text-sm font-semibold transition-colors ${
                                              isLocalEnrolled
                                                ? 'bg-secondary text-white hover:bg-red-600' 
                                                : 'bg-primary text-white hover:bg-blue-700'
                                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                                          >
                                            {loadingSessionId === sessionIdStr ? (
                                              <span className="flex items-center justify-center gap-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                {isLocalEnrolled ? 'Unenrolling...' : 'Enrolling...'}
                                              </span>
                                            ) : isLocalEnrolled ? (
                                              'Unenroll'
                                            ) : (
                                              'Enroll'
                                            )}
                                          </button>
                                        )
                                      })()}

                                  </div>
                                </div>
                              )
                            })
                          )
                          ) : (
                          <div className="text-center py-4 text-gray-500 text-sm h-[30rem] items-center flex justify-center">
                            <div className="flex flex-col items-center gap-2">
                              {!selectedSubscription ? (
                                <>
                                  <p className="text-base font-semibold text-gray-700">
                                    Please select a subscription to view sessions
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    Choose from the dropdown above to get started
                                  </p>
                                </>
                              ) : searchInstructorSessions ? (
                                <>
                                  <p className="text-base font-semibold text-gray-700">
                                    No sessions found matching "
                                    {searchInstructorSessions}"
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    Try adjusting your search terms
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-base font-semibold text-gray-700">
                                    No sessions available
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    There are currently no upcoming training 
                                    sessions
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                  </div>
                </div>
              </div>

              { !(currentUser.user_level >= 6) && (
                <div className="lg:w-1/3 w-full flex flex-col max-h-[100%]">
                  <div className="block bg-gray-100 border-2 border-black rounded p-4 mb-1">
                    <h3 className="font-bold text-gray-800 mb-2 text-sm">
                      Use this tool to enroll users in Training Sessions
                    </h3>
                    <ol
                      className="text-xs text-gray-700 space-y-1 ml-4"
                      style={{ listStyleType: 'decimal' }}
                    >
                      <li>Select the upcoming training session at left</li>
                      <li>
                        Click{' '}
                        <span className="inline-block bg-primary text-white px-2 py-0.5 rounded text-xs font-bold">
                          Enroll
                        </span>{' '}
                        below to enroll a user
                      </li>
                      <li>
                        Click{' '}
                        <span className="inline-block bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold">
                          Unenroll
                        </span>{' '}
                        below to unenroll a user
                      </li>
                    </ol>
                  </div>

                  <div className="bg-gray-100 border-2 border-black rounded p-4 flex flex-col flex-grow">
                    <div className="flex items-center gap-2 mb-3 w-full">
                      {/* Label hides when input focused */}
                      {/* {!isFocused && (
                        <h3 className="font-bold text-gray-600 whitespace-nowrap transition-all duration-300">
                          Learners:
                        </h3>
                      )} */}

                      {/* Input wrapper (relative for positioning the X) */}
                      <div
                        className={`relative transition-all duration-300 ${isFocused ? 'w-full' : 'flex-1'}`}
                      >
                        <input
                          type="text"
                          placeholder="Search: Users"
                          value={searchLearners}
                          onChange={(e) => onSearchLearnersChange(e.target.value)}
                          onFocus={() => setIsFocused(true)}
                          // onBlur={() => setIsFocused(false)}
                          className="w-full px-3 py-1 pr-8 border border-gray-400 rounded-md
                                      focus:outline-none focus:ring-2 focus:ring-gray-500
                                      bg-white text-sm text-gray-600 transition-all duration-300"
                        />

                        {isFocused && (
                          <button
                            type="button"
                            onClick={() => {
                              onSearchLearnersChange('')
                              setIsFocused(false)
                            }}
                            aria-label="Clear search"
                            className="absolute right-2 top-1/2 -translate-y-1/2 
                                        text-gray-500 hover:text-gray-700"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="bg-white border border-black rounded p-3 max-h-[24.5rem] overflow-y-auto flex-grow">
                      {loadingStudentsContent ? (
                        <div className="flex items-center justify-center w-full h-[200px]">
                          <div className="flex flex-col items-center justify-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent mb-4"></div>
                            <p className="text-lg font-semibold text-gray-800">
                              Loading Users...
                            </p>
                          </div>
                        </div>
                      ) : sortedLearners.length > 0 ? (
                        <div className="space-y-1">
                          {sortedLearners.map((learner) => {
                            const learnerIdStr = String(learner.id)
                            const isEnrolled = enrolledMap.has(learnerIdStr)
                            const isLoading = enrollingLearners.has(learnerIdStr)

                            return (
                              <div
                                key={learner.id}
                                className="flex items-center justify-between py-1.5"
                              >
                                <span className="text-gray-800 font-medium text-sm">
                                  {learner.first_name} {learner.last_name}
                                </span>

                                <button
                                  onClick={() =>
                                    isEnrolled
                                      ? onUnenroll(learner.id)
                                      : onEnroll(learner.id)
                                  }
                                  disabled={
                                    !selectedSession?.sessionId || isLoading
                                  }
                                  className={`px-4 py-1 rounded text-xs font-bold transition-colors ${isEnrolled
                                    ? 'bg-red-600 hover:bg-red-700 text-white'
                                    : 'bg-primary hover:bg-blue-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed'
                                    }`}
                                >
                                  {isLoading ? (
                                    <span className="flex items-center justify-center gap-1">
                                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                                      {isEnrolled ? 'Removing...' : 'Adding...'}
                                    </span>
                                  ) : isEnrolled ? (
                                    'Unenroll'
                                  ) : (
                                    'Enroll'
                                  )}
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          No users found
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {activeTab === 'online' && (
          <div className="w-full flex flex-col">
            <div className="flex gap-0.5">
              <button
                onClick={() => setContentTab('learning_plans')}
                className={`relative inline-block mr-[-20px] px-2 py-1 font-semibold text-black transition-all overflow-hidden
                      ${contentTab === 'learning_plans'
                    ? 'bg-gray-200 text-black'
                    : 'bg-gray-400 text-black'
                  }
                      rounded-t `}
              >
                Learning Plans
                {!loadingBranches &&
                  !loadingSubscriptions &&
                  !loadingContent &&
                  filteredLearningPlans.length > 0 && (
                    <span
                      className={`
                      ml-2 
                      inline-flex items-center justify-center 
                      text-xs font-bold 
                      rounded-full 
                      w-9 h-5
                      ${contentTab === 'learning_plans' ? 'bg-gray-400 text-black' : 'bg-gray-200 text-black'}

                    `}
                    >
                      {filteredLearningPlans.length}
                    </span>
                  )}
              </button>
              <div
                style={{
                  boxShadow:
                    contentTab === 'learning_plans'
                      ? '0px 1px rgb(229 231 235)'
                      : 'none',
                }}
                className={`relative bottom-[-20px] -ml-[-17px]`}
              >
                <CurvedTriangle
                  direction="right"
                  color={
                    contentTab === 'learning_plans'
                      ? 'rgb(229 231 235)'
                      : 'rgb(156 163 175)'
                  }
                  width={5}
                  height={12}
                />
              </div>
              <div
                className={`relative bottom-[-20px] -mr-[3px]
                      ${contentTab === 'courses'
                    ? 'border-primary'
                    : 'border-b-[0.5px] border-gray-200'
                  }`}
              >
                <CurvedTriangle
                  direction="left"
                  color={
                    contentTab === 'courses'
                      ? 'rgb(229 231 235)'
                      : 'rgb(156 163 175)'
                  }
                  width={5}
                  height={12}
                />
              </div>
              <button
                onClick={() => setContentTab('courses')}
                className={`relative inline-block px-2 py-1 font-semibold text-balck transition-all overflow-hidden
                      ${contentTab === 'courses'
                    ? 'bg-gray-200 text-black'
                    : 'bg-gray-400 text-black'
                  }
                      rounded-t`}
              >
                Courses
                {!loadingBranches &&
                  !loadingSubscriptions &&
                  !loadingContent &&
                  filteredCourses.length > 0 && (
                    <span
                      className={`
                      ml-2 
                      inline-flex items-center justify-center 
                      text-xs font-bold 
                      rounded-full 
                      w-9 h-5 
                      ${contentTab === 'courses' ? 'bg-gray-400 text-black' : 'bg-gray-200 text-black'}
                    `}
                    >
                      {filteredCourses.length}
                    </span>
                  )}
              </button>

              <div className={`relative bottom-[-20px] -ml-[3px]`}>
                <CurvedTriangle
                  direction="right"
                  color={
                    contentTab === 'courses'
                      ? 'rgb(229 231 235)'
                      : 'rgb(156 163 175)'
                  }
                  width={5}
                  height={12}
                />
              </div>
            </div>

            <div
              className={`flex lg:flex-row flex-col gap-2 w-full rounded-tl-none p-2  border-black
                      ${contentTab === 'learning_plans'
                  ? 'bg-gray-200 text-black'
                  : contentTab === 'courses'
                    ? 'bg-gray-200 text-black'
                    : 'bg-gray-200 text-black'
                }
                      rounded-t `}
            >
              <div className="flex border-2 border-black bg-gray-100 rounded p-4 shadow-md w-full lg:w-2/3 flex-grow flex-col">
                <div className=" border-gray-00 flex flex-col flex-grow">
                  <div
                    className={` text-xs text-gray-600 flex justify-end items-center mt-0 ${activeTrainingField === 'filter'
                      ? 'w-full'
                      : 'w-full md:w-[48%] pl-0'
                      }`}
                  >
                    <span className={`ml-auto mr-0`}>
                      {!loadingBranches &&
                        !loadingSubscriptions &&
                        onlineLearningSubs.length > 0 &&
                        activeTrainingField != 'search' ? (
                        <span>{onlineLearningSubs.length} Available</span>
                      ) : (
                        <span>&nbsp;</span>
                      )}
                    </span>
                  </div>

                  <div
                    ref={wrapperRef}
                    style={{
                      justifyContent:
                        activeTrainingField === 'filter'
                          ? 'flex-start'
                          : activeTrainingField === 'search'
                            ? 'flex-end'
                            : 'flex-start',
                    }}
                    className="flex flex-col md:flex-row md:items-center md:gap-4 w-full transition-all mb-2"
                  >
                    {loadingBranches ? (
                      <h3 className="text-xl font-bold flex items-center text-primary">
                        <span className="ml-1 animate-pulse">
                          Loading branches...
                        </span>
                      </h3>
                    ) : (
                      <>
                        {loadingSubscriptions ? (
                          <h3 className="text-xl font-bold flex items-center text-primary">
                            <span className="ml-1 animate-pulse">
                              Loading Subscriptions...
                            </span>
                          </h3>
                        ) : onlineLearningSubs.length == 0 ? (
                          <p className="text-lg font-semibold text-secondary">
                            No Online Training Subscriptions Found
                          </p>
                        ) : (
                          <>
                            {(activeTrainingField === null ||
                              activeTrainingField === 'filter') && (
                                <div
                                  className={`relative transition-all duration-300 xs:mb-1 md:mb-0 ${activeTrainingField === 'filter'
                                    ? 'w-full'
                                    : 'w-full md:w-1/2'
                                    }`}
                                  onMouseEnter={
                                    isMdUp
                                      ? () => setActiveTrainingField('filter')
                                      : undefined
                                  }
                                  onMouseLeave={
                                    isMdUp
                                      ? () => setActiveTrainingField(null)
                                      : undefined
                                  }
                                >
                                  <select
                                    title="Filter By Subscription"
                                    value={selectedSubscription}
                                    onChange={(e) =>
                                      onSubscriptionChange(e.target.value)
                                    }
                                    className="w-full px-3 py-1 pr-8 border border-gray-400 rounded-md 
                                      focus:outline-none focus:ring-2 focus:ring-gray-500 
                                      bg-white text-sm text-gray-700"
                                  >
                                    <option value="">
                                      ▼ Filter By Subscription
                                    </option>
                                    {onlineLearningSubs.map((sub) => (
                                      <option key={sub.id} value={sub.id}>
                                        {sub.plan_name} | {sub.plan_code}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}

                            {(activeTrainingField === null ||
                              activeTrainingField === 'search') && (
                                <div
                                  className={`relative transition-all duration-300 ${activeTrainingField === 'search'
                                    ? 'w-full'
                                    : 'w-full md:w-1/2'
                                    }`}
                                  onMouseEnter={
                                    isMdUp
                                      ? () => setActiveTrainingField('search')
                                      : undefined
                                  }
                                  onMouseLeave={
                                    isMdUp
                                      ? (e) => {
                                        if (
                                          !e.currentTarget.contains(
                                            document.activeElement
                                          ) &&
                                          activeTrainingField === 'search'
                                        ) {
                                          setActiveTrainingField(null)
                                        }
                                      }
                                      : undefined
                                  }
                                >
                                  <input
                                    type="text"
                                    placeholder="Search Sessions"
                                    value={searchContent}
                                    onChange={(e) =>
                                      setSearchContent(e.target.value)
                                    }
                                    className="w-full px-3 py-1 pr-8 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white text-sm text-gray-600"
                                  />

                                  {activeTrainingField === 'search' && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSearchContent('')
                                        setActiveTrainingField(null)
                                      }}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                      <X size={16} />
                                    </button>
                                  )}
                                </div>
                              )}
                          </>
                        )}
                      </>
                    )}
                  </div>

                  <div className="bg-gray-50 border border-gray-300 rounded p-2 max-h-[20rem] lg:max-h-[33rem] overflow-y-auto flex flex-col flex-grow">
                    <div className="space-y-1.5 flex-grow">
                      {loadingContent ? (
                        <div className="flex items-center justify-center w-full h-[31rem]">
                          <div className="flex flex-col items-center justify-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent mb-4"></div>
                            <p className="text-lg font-semibold text-gray-800">
                              Loading{' '}
                              {contentTab === 'courses'
                                ? 'courses'
                                : 'learning plans'}
                              ...
                            </p>
                          </div>
                        </div>
                      ) :
                        onlineLearningSubs.length == 0 ? (
                          <div className="text-center py-8 text-gray-500 text-sm h-[31rem] items-center flex justify-center">
                            <div className="flex flex-col items-center gap-2">
                              <>
                                <p className="text-base font-semibold text-gray-700">
                                  No {contentTab === 'courses' ? 'Courses' : 'Learning Plans'} Available
                                </p>
                              </>
                            </div>
                          </div>
                        ) :
                          currentContent.length > 0 ? (
                            currentContent.map((item) => (
                              <label
                                key={item.item_id}
                                className={`flex items-start gap-2 p-1.5 rounded cursor-pointer transition-all border ${selectedContent.includes(item.item_id)
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-white border-gray-200 hover:border-gray-300'
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedContent.includes(item.item_id)}
                                  onChange={() => handleContentToggle(item.item_id)}
                                  className="w-4 h-4 mt-0.5 cursor-pointer accent-blue-600 flex-shrink-0"
                                />
                                <span className="text-gray-800 text-sm leading-snug">
                                  {item.item_name}
                                </span>
                              </label>
                            ))
                          ) : (
                            <div className="text-center py-8 text-gray-500 text-sm h-[31rem] items-center flex justify-center">
                              <div className="flex flex-col items-center gap-2">
                                {!selectedSubscription ? (
                                  <>
                                    <p className="text-base font-semibold text-gray-700">
                                      Please select a subscription to view{' '}
                                      {contentTab === 'courses'
                                        ? 'courses'
                                        : 'learning plans'}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      Choose from the dropdown above to get started
                                    </p>
                                  </>
                                ) : searchContent ? (
                                  <>
                                    <p className="text-base font-semibold text-gray-700">
                                      No{' '}
                                      {contentTab === 'courses'
                                        ? 'courses'
                                        : 'learning plans'}{' '}
                                      found matching "{searchContent}"
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      Try adjusting your search terms
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-base font-semibold text-gray-700">
                                      No{' '}
                                      {contentTab === 'courses'
                                        ? 'courses'
                                        : 'learning plans'}{' '}
                                      available
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      There are currently no{' '}
                                      {contentTab === 'courses'
                                        ? 'courses'
                                        : 'learning plans'}{' '}
                                      in this subscription
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                          )}           
                    </div>
                  </div>
                  {currentUser.user_level == 6 && (
                    <div className="flex justify-end mt-4">
                      <button onClick={() => handleEnrollOLTTechnician()} className="bg-primary text-white px-4 py-1.5 rounded text-sm font-semibold">
                        {isEnrolling ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Enrolling...
                        </span>
                      ) : (
                        'Enroll'
                      )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {!(currentUser.user_level >= 6) && (

                <div className="w-full lg:w-1/3 bg-transparent flex flex-col">
                  <div className="block bg-gray-100 border-2 border-black rounded p-4 mb-2 shadow-sm">
                    <h3 className="font-bold text-gray-800 mb-2 text-sm">
                      Use this tool to enroll users in Learning Plans
                    </h3>
                    <ol
                      className="text-xs text-gray-700 space-y-1.5 ml-4"
                      style={{ listStyleType: 'decimal' }}
                    >
                      <li>Select Learning Plan(s) at left</li>
                      <li>Choose Learner(s) that you wish to enroll</li>
                      <li>Click "Enroll Users"</li>
                    </ol>
                  </div>

                  <div className="bg-gray-100 border-2 border-black rounded p-4 shadow-md flex-grow">
                    <div className="flex items-center gap-2 mb-3 w-full">
                      {/* {!isFocused && (
                        <h3 className="font-bold text-gray-600 whitespace-nowrap transition-all duration-300">
                          Learners:
                        </h3>
                      )} */}

                      <div
                        className={`relative transition-all duration-300 ${isFocused ? 'w-full' : 'flex-1'}`}
                      >
                        <input
                          type="text"
                          placeholder="Search: Users"
                          value={searchLearners}
                          onChange={(e) => onSearchLearnersChange(e.target.value)}
                          onFocus={() => setIsFocused(true)}
                          className="w-full px-3 py-1 pr-8 border border-gray-400 rounded
                                    focus:outline-none focus:ring-2 focus:ring-gray-500 
                                    bg-white text-sm text-gray-600 transition-all duration-300"
                        />

                        {isFocused && (
                          <button
                            title="Close modal"
                            type="button"
                            onClick={() => {
                              onSearchLearnersChange('')
                              setIsFocused(false)
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 
                                            text-gray-500 hover:text-gray-700"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-300 rounded p-3 max-h-80 overflow-y-auto">
                      <div className="space-y-1.5">
                        {loadingStudentsContent ? (
                          <div className="flex items-center justify-center w-full h-[200px]">
                            <div className="flex flex-col items-center justify-center">
                              <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent mb-4"></div>
                              <p className="text-lg font-semibold text-gray-800">
                                Loading Users...
                              </p>
                            </div>
                          </div>
                        ) : filteredLearnersList.length > 0 ? (
                          filteredLearnersList.map((learner) => (
                            <label
                              key={learner.id}
                              className={`flex items-center gap-2 p-2 rounded transition-all ${selectedContent.length === 0
                                ? 'cursor-not-allowed opacity-60'
                                : selectedLearners.includes(learner.id)
                                  ? 'bg-blue-50 cursor-pointer'
                                  : 'bg-white hover:bg-gray-100 cursor-pointer'
                                }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedLearners.includes(learner.id)}
                                onChange={() => handleLearnerToggle(learner.id)}
                                disabled={selectedContent.length === 0}
                                className={`w-4 h-4 accent-blue-600 flex-shrink-0 ${selectedContent.length === 0
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'cursor-pointer'
                                  }`}
                              />
                              <span className="text-gray-800 text-sm">
                                {learner.first_name} {learner.last_name}
                              </span>
                            </label>
                          ))
                        ) : (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            No users found
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={handleEnrollClick}
                      disabled={isEnrollDisabled}
                      className={`w-full mt-4 px-4 py-2.5 rounded font-semibold text-white text-sm transition-all ${isEnrollDisabled
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary hover:bg-blue-600 active:scale-[0.98]'
                        }`}
                    >
                      {isEnrolling ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Enrolling...
                        </span>
                      ) : (
                        'Enroll Users'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EnrollmentTabs
